'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { ReplyPreview } from '@/components/chat/chat-input';

/**
 * Per-room chat composition draft state.
 *
 * Lifted out of `ChatInput`'s local `useState` so a draft (typed text,
 * cursor position, in-flight reply target, pending @-mention map)
 * survives the host unmount: closing the desktop chat sheet, switching
 * the mobile chip-rail back to the canvas, navigating off `/chat`, or
 * board-switching all dropped the draft before this provider existed.
 *
 * Persistence: each room's draft is mirrored to `sessionStorage` so a
 * hard refresh during composition also keeps the message — same scope
 * as the rest of the chat session state (`/chat`'s last-room key, etc.).
 *
 * Per-room keying ensures switching boards doesn't leak a draft from
 * one board's chat into another's.
 */
export interface ChatDraft {
  text: string;
  cursor: number;
  replyTo: ReplyPreview | null;
  /** Display label → resolved address map for pending @-mentions. The
   *  user types `@alice` (the display label they see in the autocomplete
   *  popover) and we transform it into `@[0x…]` before sending; this
   *  map is the lookup. */
  mentions: Record<string, string>;
}

const EMPTY_DRAFT: ChatDraft = Object.freeze({
  text: '',
  cursor: 0,
  replyTo: null,
  mentions: {},
});

const STORAGE_PREFIX = 'takeover-chat-draft:';

interface ChatDraftContextValue {
  /** Read the current draft for a room. Returns the empty draft when
   *  the room has no in-flight composition. Stable identity across
   *  unrelated draft updates so callers can use it as a `useEffect`
   *  dep without churn. */
  get(room: string): ChatDraft;
  /** Patch a room's draft. Pass any subset of `ChatDraft` keys —
   *  unspecified keys retain their current value. Empty patches are
   *  no-ops. */
  set(room: string, patch: Partial<ChatDraft>): void;
  /** Clear a room's draft (e.g. after a successful send). */
  clear(room: string): void;
}

const ChatDraftContext = createContext<ChatDraftContextValue | null>(null);

function readFromStorage(room: string): ChatDraft | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(`${STORAGE_PREFIX}${room}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ChatDraft>;
    return {
      text: typeof parsed.text === 'string' ? parsed.text : '',
      cursor: typeof parsed.cursor === 'number' ? parsed.cursor : 0,
      replyTo: parsed.replyTo ?? null,
      mentions:
        parsed.mentions && typeof parsed.mentions === 'object'
          ? (parsed.mentions as Record<string, string>)
          : {},
    };
  } catch {
    return null;
  }
}

function writeToStorage(room: string, draft: ChatDraft) {
  if (typeof window === 'undefined') return;
  try {
    if (
      draft.text === '' &&
      draft.replyTo === null &&
      Object.keys(draft.mentions).length === 0
    ) {
      window.sessionStorage.removeItem(`${STORAGE_PREFIX}${room}`);
      return;
    }
    window.sessionStorage.setItem(
      `${STORAGE_PREFIX}${room}`,
      JSON.stringify(draft),
    );
  } catch {
    // sessionStorage can throw under quota / Safari private — drop.
  }
}

export function ChatDraftProvider({ children }: { children: ReactNode }) {
  // The store is a single Record keyed by room. We bump a version
  // counter on every mutation so subscribers rerender; the actual draft
  // map lives in a ref to avoid churn on the value identity that would
  // re-render every consumer when an unrelated room's draft changes.
  // The version itself is wired into the context `value` memo below so
  // a `bump()` actually propagates to `useContext` consumers — without
  // that dep the value's identity is permanently stable (`get`/`set`/
  // `clear` are themselves stable refs) and React skips notifying
  // consumers, leaving `ChatInput`'s controlled textarea pinned to
  // whatever `draft.text` was at the last incidental ancestor render.
  // The user-visible symptom of dropping the dep is that you can type
  // one or two characters (until an unrelated ancestor re-render flushes
  // through) and then the input freezes mid-composition.
  const draftsRef = useRef<Record<string, ChatDraft>>({});
  const [version, bumpVersion] = useState(0);
  const bump = useCallback(() => bumpVersion((v) => v + 1), []);

  // Hydrate from sessionStorage on mount. We don't sweep storage for
  // every key — readFromStorage runs lazily inside `get(room)` the
  // first time a room is asked for.

  const get = useCallback((room: string): ChatDraft => {
    const cached = draftsRef.current[room];
    if (cached) return cached;
    const stored = readFromStorage(room);
    if (stored) {
      draftsRef.current[room] = stored;
      return stored;
    }
    draftsRef.current[room] = EMPTY_DRAFT;
    return EMPTY_DRAFT;
  }, []);

  const set = useCallback(
    (room: string, patch: Partial<ChatDraft>) => {
      const prev = draftsRef.current[room] ?? readFromStorage(room) ?? EMPTY_DRAFT;
      const next: ChatDraft = {
        text: patch.text ?? prev.text,
        cursor: patch.cursor ?? prev.cursor,
        replyTo: patch.replyTo === undefined ? prev.replyTo : patch.replyTo,
        mentions: patch.mentions ?? prev.mentions,
      };
      // Reference-equal patch is a no-op; saves a render.
      if (
        next.text === prev.text &&
        next.cursor === prev.cursor &&
        next.replyTo === prev.replyTo &&
        next.mentions === prev.mentions
      ) {
        return;
      }
      draftsRef.current[room] = next;
      writeToStorage(room, next);
      bump();
    },
    [bump],
  );

  const clear = useCallback(
    (room: string) => {
      if (
        !draftsRef.current[room] &&
        (typeof window === 'undefined' ||
          !window.sessionStorage.getItem(`${STORAGE_PREFIX}${room}`))
      ) {
        return;
      }
      draftsRef.current[room] = EMPTY_DRAFT;
      writeToStorage(room, EMPTY_DRAFT);
      bump();
    },
    [bump],
  );

  const value = useMemo<ChatDraftContextValue>(
    () => ({ get, set, clear }),
    [get, set, clear, version],
  );

  return (
    <ChatDraftContext.Provider value={value}>
      {children}
    </ChatDraftContext.Provider>
  );
}

/**
 * Hook for `ChatInput`-style consumers. Returns the live draft for the
 * given room plus stable mutators. The returned `draft` reference
 * changes whenever any room's draft mutates (we don't memo per-room
 * because `ChatInput` is mounted at most once per route, so the cost is
 * negligible) — pass `draft.text` etc. as deps if you only care about a
 * single field.
 */
export function useChatDraft(room: string) {
  const ctx = useContext(ChatDraftContext);
  if (!ctx) {
    throw new Error('useChatDraft must be used within ChatDraftProvider');
  }
  const draft = ctx.get(room);
  const setDraft = useCallback(
    (patch: Partial<ChatDraft>) => ctx.set(room, patch),
    [ctx, room],
  );
  const clearDraft = useCallback(() => ctx.clear(room), [ctx, room]);
  return { draft, setDraft, clearDraft };
}

// Re-export so tests / dev tools can introspect the shape without
// importing from the chat-input file (which has heavy deps).
export type { ChatDraftContextValue };
